import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useRole } from '../../hooks/useRole';
import { useUserData } from '../../hooks/useUserData';
import { auth } from '../../firebase/config';
import { createAuditLog } from '../../redux/thunks/auditThunks';
import { UserRole, RoleUtils } from '../../types/enums';
import { 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

// Import macro service functions (to be created)
import { getAllMacros, createMacro, updateMacro, deleteMacro } from '../../services/macroService';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const Macros = () => {
  const { hasRole } = useRole();
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMacro, setEditingMacro] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    code: '',
    expansionText: ''
  });

  const dispatch = useDispatch();
  const { user } = useUserData();

  const filteredMacros = useMemo(() => {
    if (!macros || !Array.isArray(macros)) {
      return [];
    }
    
    return macros.filter(macro => {
      const searchLower = searchQuery.toLowerCase();
      return (
        macro.code?.toLowerCase().includes(searchLower) ||
        macro.expansionText?.toLowerCase().includes(searchLower)
      );
    });
  }, [macros, searchQuery]);

  const paginatedMacros = useMemo(() => {
    return filteredMacros.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredMacros, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    // Only fetch macros if user is authenticated
    if (user) {
      fetchMacros();
    }
  }, [user]); // Add user as dependency

  const fetchMacros = async () => {
    try {
      setLoading(true);
      setError('');
      
      const macroList = await getAllMacros();
      
      if (Array.isArray(macroList)) {
        setMacros(macroList);
      } else {
        setMacros([]);
      }
    } catch (error) {
      console.error('Macros component: Error fetching macros:', error);
      setError(`Failed to fetch macros: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMacro = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!hasRole(UserRole.ADMIN)) {
      setError('Only administrators can add new macros');
      setLoading(false);
      return;
    }

    try {
      // Check if code already exists
      const existingMacro = macros.find(macro => macro.code === formData.code);
      if (existingMacro) {
        setError('A macro with this code already exists');
        setLoading(false);
        return;
      }

      const newMacro = {
        code: formData.code.trim(),
        expansionText: formData.expansionText.trim()
      };

      const result = await createMacro(newMacro);

      await dispatch(createAuditLog({
        userId: auth.currentUser.uid,
        action: 'CREATE_MACRO',
        targetId: result.id,
        targetType: 'MACRO',
        details: {
          code: newMacro.code,
          expansionText: newMacro.expansionText
        }
      }));

      setSuccess('Macro created successfully');
      setFormData({ code: '', expansionText: '' });
      setDialogOpen(false);
      await fetchMacros();
    } catch (error) {
      console.error('Error adding macro:', error);
      setError('Failed to create macro');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMacro = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!hasRole(UserRole.ADMIN)) {
      setError('Only administrators can edit macros');
      setLoading(false);
      return;
    }

    try {
      // Check if code already exists (excluding current macro)
      const existingMacro = macros.find(macro => 
        macro.code === formData.code && macro.id !== editingMacro.id
      );
      if (existingMacro) {
        setError('A macro with this code already exists');
        setLoading(false);
        return;
      }

      const updatedMacro = {
        code: formData.code.trim(),
        expansionText: formData.expansionText.trim()
      };

      await updateMacro(editingMacro.id, updatedMacro);

      await dispatch(createAuditLog({
        userId: auth.currentUser.uid,
        action: 'UPDATE_MACRO',
        targetId: editingMacro.id,
        targetType: 'MACRO',
        details: {
          before: {
            code: editingMacro.code,
            expansionText: editingMacro.expansionText
          },
          after: updatedMacro
        }
      }));

      setSuccess('Macro updated successfully');
      setFormData({ code: '', expansionText: '' });
      setEditingMacro(null);
      setDialogOpen(false);
      await fetchMacros();
    } catch (error) {
      console.error('Error updating macro:', error);
      setError('Failed to update macro');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMacro = async (macroId) => {
    if (!hasRole(UserRole.ADMIN)) {
      setError('Only administrators can delete macros');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this macro?')) {
      return;
    }

    try {
      const macroToDelete = macros.find(macro => macro.id === macroId);
      await deleteMacro(macroId);

      await dispatch(createAuditLog({
        userId: auth.currentUser.uid,
        action: 'DELETE_MACRO',
        targetId: macroId,
        targetType: 'MACRO',
        details: {
          code: macroToDelete.code,
          expansionText: macroToDelete.expansionText
        }
      }));

      setSuccess('Macro deleted successfully');
      await fetchMacros();
    } catch (error) {
      console.error('Error deleting macro:', error);
      setError('Failed to delete macro');
    }
  };

  const openDialog = (macro = null) => {
    if (macro) {
      setEditingMacro(macro);
      setFormData({
        code: macro.code,
        expansionText: macro.expansionText
      });
    } else {
      setEditingMacro(null);
      setFormData({ code: '', expansionText: '' });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMacro(null);
    setFormData({ code: '', expansionText: '' });
    setError('');
  };

  const theme = useTheme();

  if (loading && macros.length === 0) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Check if user has admin access using RoleUtils
  const hasAdminAccess = hasRole(UserRole.ADMIN);
  
  if (!hasAdminAccess) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1">
              You need administrator privileges to access the Macros management page.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Current roles: {user?.roles ? user.roles.join(', ') : 'None'}
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Macros
          </Typography>
        </CardContent>
      </HeaderCard>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

             {success && (
         <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccess('')}>
           {success}
         </Alert>
       )}



      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                         <Typography variant="h6" sx={{ fontWeight: 600 }}>
               Macros ({filteredMacros.length}) - Total: {macros.length}
             </Typography>
                         <Button
               variant="contained"
               startIcon={<AddIcon />}
               onClick={() => openDialog()}
               disabled={!hasRole(UserRole.ADMIN)}
               sx={{
                 borderRadius: '12px',
                 textTransform: 'none',
                 fontWeight: 600,
                 px: 3,
                 py: 1.5,
               }}
             >
               Add Macro
             </Button>
          </Box>

                     <TextField
             placeholder="Search macros..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
             variant="outlined"
             size="small"
             sx={{ minWidth: 300, mb: 3 }}
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <SearchIcon />
                 </InputAdornment>
               ),
             }}
           />

          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Code</StyledTableCell>
                  <StyledTableCell>Expansion Text</StyledTableCell>
                  <StyledTableCell>Created</StyledTableCell>
                  <StyledTableCell>Updated</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
                             <TableBody>
                 {paginatedMacros.map((macro) => (
                   <TableRow 
                     key={macro.id}
                     sx={{ 
                       '&:hover': { 
                         backgroundColor: theme.palette.action.hover 
                       } 
                     }}
                   >
                    <TableCell>
                      <Box
                        component="code"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        {macro.code}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                        {macro.expansionText}
                      </Typography>
                    </TableCell>
                                         <TableCell>
                       {macro.createdAt?.toDate ? 
                         macro.createdAt.toDate().toLocaleDateString() : 
                         macro.createdAt?._seconds ? 
                           new Date(macro.createdAt._seconds * 1000).toLocaleDateString() :
                           new Date(macro.createdAt).toLocaleDateString()
                       }
                     </TableCell>
                     <TableCell>
                       {macro.updatedAt?.toDate ? 
                         macro.updatedAt.toDate().toLocaleDateString() : 
                         macro.updatedAt?._seconds ? 
                           new Date(macro.updatedAt._seconds * 1000).toLocaleDateString() :
                           new Date(macro.updatedAt).toLocaleDateString()
                       }
                     </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                                                 <ActionButton 
                           size="small" 
                           onClick={() => openDialog(macro)}
                           disabled={!hasRole(UserRole.ADMIN)}
                           startIcon={<EditIcon />}
                           variant="outlined"
                           color="primary"
                         >
                           Edit
                         </ActionButton>
                                                 <ActionButton 
                           size="small" 
                           color="error" 
                           onClick={() => handleDeleteMacro(macro.id)}
                           disabled={!hasRole(UserRole.ADMIN)}
                           startIcon={<DeleteIcon />}
                           variant="outlined"
                         >
                           Delete
                         </ActionButton>
                      </Box>
                                         </TableCell>
                   </TableRow>
                 ))}
                              </TableBody>
             </Table>
             
             {paginatedMacros.length === 0 && !loading && (
               <Box sx={{ p: 4, textAlign: 'center' }}>
                 <Typography variant="body1" color="text.secondary">
                   {macros.length === 0 ? 'No macros found. Create your first macro to get started.' : 'No macros match your search criteria.'}
                 </Typography>
               </Box>
             )}
             
             <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredMacros.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </StyledTableContainer>
        </CardContent>
      </ModernCard>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingMacro ? 'Edit Macro' : 'Add New Macro'}
          </Typography>
        </DialogTitle>
        <form onSubmit={editingMacro ? handleEditMacro : handleAddMacro}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                fullWidth
                required
                helperText="Unique identifier for the macro (e.g., 'greeting', 'signature')"
                placeholder="Enter macro code"
              />
              <TextField
                label="Expansion Text"
                value={formData.expansionText}
                onChange={(e) => setFormData({ ...formData, expansionText: e.target.value })}
                fullWidth
                required
                multiline
                rows={4}
                helperText="Text that will replace the macro when used"
                placeholder="Enter the text that should replace this macro"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={closeDialog}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !formData.code.trim() || !formData.expansionText.trim()}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              {loading ? <CircularProgress size={20} /> : (editingMacro ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Macros; 